import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { CompleteBlueIcon } from "../../../../../assets";
import {
  ProjectPermissionConstants,
  ResourcePermissionConstants,
  WorkflowOrganisationAccessLevel,
} from "../../../../../constants";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin, ResultType, SelectData, TaskPanelData } from "../../../../../models";
import {
  GetProjectDetailsResponse,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksRequest,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { getTaskBoard } from "../../../../../service/query/QueryService.full";
import { FilterGroups, ResultData, Status } from "../../../../../service/Shared";
import { useTaskTagsFilter } from "../../../../../templates/TasksGeneralView";
import { useDeepLinkedTask } from "../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../useAuth";
import { flattenObject, hasWorkflowOrganisationAccessLevel, useUpdateEffect } from "../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../../../../widget";
import { AvatarCellFormatter } from "../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";
import { ProjectContext } from "../ProjectContext";

interface UseTasksReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  projectDetails: GetProjectDetailsResponse | undefined;

  showSelectWorkflowTemplateModal: boolean;
  setShowSelectWorkflowTemplateModal: Dispatch<SetStateAction<boolean>>;

  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;

  showWorkflowTaskPanel: boolean;
  setShowWorkflowTaskPanel: Dispatch<SetStateAction<boolean>>;
  handleCloseWorkflowTaskPanel: () => void;
  // Create-mode task type (REQ-010): true when the user chose to create a general task.
  createTaskIsGeneral: boolean;
  showNewTaskModal: boolean;
  setShowNewTaskModal: Dispatch<SetStateAction<boolean>>;
  handleNewTask: (isGeneral: boolean) => void;

  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  selectedCompleteTask: TaskPanelData | undefined;

  workflow: GetTaskBoardResponse["results"][number] | undefined;

  // True when the API would accept a task create from this user, so the header actions can be shown.
  canCreateTask: boolean;
  // General tasks are standalone, so this holds whether or not the project has a workflow.
  canCreateGeneralTask: boolean;
  // Workflow tasks additionally need the project to have a workflow, so this folds that in.
  canCreateWorkflowTask: boolean;
  // Applying a workflow template to the project — unrelated to task creation, so it keeps its own gate.
  canApplyWorkflow: boolean;

  refreshTasks: boolean;
  handleRefresh: () => void;

  tagsFilterSelectData: SelectData;
  tagsFilterValues: string[];
  onTagsFilter: (keys: string[]) => void;

  navigateToTasksBoard: () => void;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useDocuments = (): UseTasksReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType, hasPermission } = useAuth();
  const { projectDetails, hasProjectPermission } = useContext(ProjectContext);

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number] | undefined>();
  // Id of the latest workflow load, so a response for a superseded project is ignored.
  const workflowRequestIdRef = useRef(0);

  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const [showSelectWorkflowTemplateModal, setShowSelectWorkflowTemplateModal] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [createTaskIsGeneral, setCreateTaskIsGeneral] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);

  const handleCloseCompleteTaskModal = (): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  };

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setCreateTaskIsGeneral(false);
    setShowWorkflowTaskPanel(false);
  };

  // Confirming the New task modal opens the overlay in create mode for the chosen task type (REQ-010).
  const handleNewTask = (isGeneral: boolean): void => {
    setShowNewTaskModal(false);
    setSelectedPanelTask(undefined);
    setCreateTaskIsGeneral(isGeneral);
    setShowWorkflowTaskPanel(true);
  };

  const handleRefresh = (): void => {
    setRefreshTasks((prev) => !prev);
  };

  /*
    A notification for a task links to this tab naming the task in ?taskUuid= (KAN-9609), so the user
    lands on the task itself rather than having to find its row in the grid.
  */
  const deepLinkedTask = useDeepLinkedTask();
  useEffect(() => {
    if (!deepLinkedTask) return;
    setSelectedPanelTask(deepLinkedTask);
    setShowWorkflowTaskPanel(true);
  }, [deepLinkedTask]);

  // refreshTasks doubles as the tag-filter refresh key: saving a task can create new org-level tags,
  // which have to appear in the filter alongside the reloaded grid.
  const { tagsFilterSelectData, tagsFilterValues, tagTaskUuids, onTagsFilter } = useTaskTagsFilter(
    handleRefresh,
    refreshTasks
  );

  /*
    Mirror the API's create-task gates so the header actions match what createWorkflowTask will accept:
    - general task (GeneralTaskCreationStrategy) — createResource on the creating org, plus readProject on
      the parent project;
    - workflow-step task (WorkflowStepTaskCreationStrategy) — createWorkflowElements on the workflow, which
      the ACL grants to Contributor and above.
    Either one is enough to open the New task modal, which then offers only the permitted types.
  */
  const canCreateGeneralTask =
    hasPermission(ResourcePermissionConstants.CREATE_RESOURCE) &&
    hasProjectPermission(ProjectPermissionConstants.READ_PROJECT);
  const workflowAccessLevel = workflow?.organisations?.find(
    (organisation) => organisation.organisationUuid === currentOrganisationUuid
  )?.accessLevel as WorkflowOrganisationAccessLevel | undefined;
  const canCreateWorkflowTask = hasWorkflowOrganisationAccessLevel(
    WorkflowOrganisationAccessLevel.Contributor,
    workflowAccessLevel
  );
  const canCreateTask = canCreateGeneralTask || canCreateWorkflowTask;

  // Applying a workflow is a project-details write, not a task create, so it keeps the original gate.
  const canApplyWorkflow = hasProjectPermission(ProjectPermissionConstants.WRITE_PROJECT_DETAILS);

  /*
    A workflow task also needs a workflow to hang its step off, so it is only creatable on a project that
    has one. General tasks are standalone (null step) and need no workflow. "New task" always opens the
    modal — it offers only the permitted types, and it is where further task setup will live.
  */
  const canCreateWorkflowTaskOnProject = projectDetails?.workflowUuid != null && canCreateWorkflowTask;

  // Reload the grid when the project changes in place (the tab can stay mounted across a :projectUuid change).
  useUpdateEffect(() => setRefreshTasks((prev) => !prev), [projectDetails?.uuid]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "nameLink",
      dataType: "string",
      formatter: "buttonLink",
      minWidth: 300,
      filterable: true,
      sortable: true,
    },
    {
      // FE-derived from step nullability (general tasks have no workflow step).
      name: "Type",
      key: "type",
      dataType: "string",
      formatter: "stringWithEllipsis",
      minWidth: 110,
      filterable: false,
      sortable: false,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
    },
    {
      key: "status",
      name: "Status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
      minWidth: 140,
    },
    {
      key: "deadlineDate",
      name: "Deadline",
      dataType: "Date",
      formatter: "dateOnlyWithIndicator",
      minWidth: 140,
      filterable: false,
      sortable: true,
    },
    {
      key: "updatedAt",
      name: "Date updated",
      dataType: "Date",
      formatter: "dateOnly",
      filterable: false,
      minWidth: 140,
    },
    {
      name: "Complete",
      key: "completeIcon",
      dataType: "boolean",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    },
  ];

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];
  const navigateToTasksBoard = (): void => {
    if (projectDetails?.workflowUuid) {
      navigate(
        getWorkflowTasksBoardRoute(projectDetails.workflowUuid, currentUserType, { origin: Origin.ProjectTasksPage })
      );
    }
  };

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };

        result.avatar = { fullName: d.assignee?.name };
        result.type = d.step == null ? TaskConstants.TASK_TYPE_GENERAL : TaskConstants.TASK_TYPE_WORKFLOW;
        result.nameLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (d.status !== TaskConstants.TASK_STATUS_UNASSIGNED)
          result.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: d.deadlineDate,
                isMandatory: d.isMandatory,
                isMilestone: d.isMilestone,
                keyDate: d.keyDate,
                status: d.status,
                repeat: d.repeat,
                description: d.description,
                name: d.name,
                visibility: [],
                assignedToUserUuid: d.assignee?.uuid ?? null,
                assignedToUserName: d.assignee?.name ?? null,
                ownerOrganisationUuid: d.ownerOrganisation.uuid,
                relateToActivity: d.objectType === "Activity",
                activityUuid: d.objectType === "Activity" ? d.objectUuid : null,
                objectType: d.objectType,
                objectUuid: d.objectUuid,
                stepUuid: d.step?.uuid ?? null,
                phase: d.step?.phase ?? null,
                uuid: d.uuid,
                rowVersion: d.rowVersion,
                resolution: d.resolution,
                reminderDate: d.reminderDate,
                reminderType: d.reminderType,
                addReminder: d.reminderDate != null,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (projectDetails === undefined) {
      return data;
    }

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.nameLink) {
      filterCriteria.name = filterCriteria.nameLink;
      filterCriteria.nameLink = undefined;
      delete filterCriteria.nameLink;
    }

    // Tag filter: restrict to the WorkflowTask uuids linked to the selected tags.
    if (tagTaskUuids !== undefined) {
      filterCriteria.uuid = { operator: "in", value: tagTaskUuids };
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    const sortKeyMap: Record<string, string> = {
      nameLink: "name",
      avatar: "assignee.name",
    };

    const request: SearchWorkflowTasksRequest & FilterGroups<SearchWorkflowTasksRequest["filter"]> = {
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
      // A task belongs to this project either via its workflow (workflow tasks carry the project
      // on workflowObjectUuid) or directly (general tasks have no workflow step and hang off the
      // project through objectType/objectUuid), so match on either.
      filterGroups: [
        {
          operator: "or",
          filter: {
            results: {
              workflowObjectUuid: { operator: "eq", value: projectDetails.uuid },
              objectUuid: { operator: "eq", value: projectDetails.uuid },
            },
          },
        },
      ],
    };

    await searchWorkflowTasks(request, {
      operator: "nin",
      value: [TaskConstants.TASK_STATUS_COMPLETE],
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  const getAndSetWorkflow = async (projectUuid: string): Promise<void> => {
    // Monotonic stamp: a response for a project the tab has moved on from must not be applied.
    workflowRequestIdRef.current += 1;
    const requestId = workflowRequestIdRef.current;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterResults: any = {
      projectUuid: {
        operator: "eq",
        value: projectUuid,
      },
    };

    const res = await getTaskBoard({
      showComplete: true,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: filterResults,
      },
    });

    if (requestId !== workflowRequestIdRef.current) return;

    if (res.status === Status.Success && res.data) {
      setWorkflow(res.data.results[0]);
    }
  };

  useEffect(() => {
    /*
      Drop the previous project's workflow before loading the new one. The tab stays mounted across a
      :projectUuid change, and holding the old workflow would let a task created in the meantime be
      parented to the wrong project — the stamp above then discards the superseded response.
    */
    setWorkflow(undefined);

    if (projectDetails?.uuid) {
      getAndSetWorkflow(projectDetails.uuid);
    }
  }, [projectDetails?.uuid]);

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    projectDetails,

    showCompleteTaskModal,
    handleCloseCompleteTaskModal,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    createTaskIsGeneral,
    showNewTaskModal,
    setShowNewTaskModal,
    handleNewTask,

    showSelectWorkflowTemplateModal,
    setShowSelectWorkflowTemplateModal,

    selectedPanelTask,
    selectedCompleteTask,
    workflow,
    canCreateTask,
    canCreateGeneralTask,
    canCreateWorkflowTask: canCreateWorkflowTaskOnProject,
    canApplyWorkflow,

    refreshTasks,
    handleRefresh,

    tagsFilterSelectData,
    tagsFilterValues,
    onTagsFilter,

    navigateToTasksBoard,
    onChange,
  };
};
